import { AlertTriangle, User, UserX } from 'lucide-react';
import { cn } from '../../ui';
import type { PersonLane } from '../../features/sprintboard/models/byPersonModel';
import type { SubtaskProgress } from '../../features/tasks/taskModel';
import type { BoardStatus, Task } from '../../features/tasks/types';
import type { DirectoryPerson } from '../../features/teams/types';
import { SprintBoardCard } from './SprintBoardCard';

export interface SprintBoardByPersonProps {
  lanes: PersonLane[];
  /** Subtask roll-up keyed by parent id (for the card badge). */
  subtaskProgress: Map<string, SubtaskProgress>;
  /** Resolved goal titles keyed by goal id. */
  goalNames: Map<string, string>;
  onOpen: (task: Task) => void;
  // Inline (drag-free) actions, forwarded to each card. All optional.
  onSetStatus?: (task: Task, boardStatus: BoardStatus) => void;
  onToggleBlocked?: (task: Task) => void;
  onRemoveFromSprint?: (task: Task) => void;
  onToggleAdhoc?: (task: Task) => void;
  /** Assigns/clears a task's owner from the card menu. */
  onAssignOwner?: (task: Task, owner: string) => void;
  /** Directory people for the assign-owner control. */
  people?: DirectoryPerson[];
  busy?: boolean;
}

/** A thin capacity bar (assigned vs capacity); turns amber when over. */
function CapacityBar({
  assigned,
  capacity,
  over,
}: {
  assigned: number;
  capacity: number;
  over: boolean;
}) {
  const pct = capacity > 0 ? Math.min(100, (assigned / capacity) * 100) : 0;
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-3">
      <div
        className={cn('h-full rounded-full', over ? 'bg-amber-400' : 'bg-accent')}
        style={{ width: `${Math.max(0, pct)}%` }}
      />
    </div>
  );
}

/**
 * The Sprint Board's "By Person" view: one lane per team member with ≥1 assigned
 * top-level task (ordered by assigned effort desc), plus a trailing Unassigned
 * lane. Each header shows the person's name + role, assigned effort vs capacity
 * with a bar + a non-blocking ⚠ over-capacity indicator, total story points, and
 * TODO / IN_PROGRESS / DONE / blocked counts. Cards reuse {@link SprintBoardCard}
 * (static, click-to-open) and keep the blocked + subtask badges.
 */
export function SprintBoardByPerson({
  lanes,
  subtaskProgress,
  goalNames,
  onOpen,
  onSetStatus,
  onToggleBlocked,
  onRemoveFromSprint,
  onToggleAdhoc,
  onAssignOwner,
  people,
  busy,
}: SprintBoardByPersonProps) {
  return (
    <div className="space-y-4">
      {lanes.map((lane) => (
        <section
          key={lane.isUnassigned ? '__unassigned__' : lane.alias}
          className="rounded-2xl border border-surface-border bg-surface-2/50 p-4"
        >
          <header className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                {lane.isUnassigned ? (
                  <UserX className="h-4 w-4 shrink-0 text-zinc-500" aria-hidden="true" />
                ) : (
                  <User className="h-4 w-4 shrink-0 text-zinc-500" aria-hidden="true" />
                )}
                <span className="truncate text-sm font-semibold text-zinc-100">
                  {lane.name}
                </span>
                {!lane.isUnassigned && lane.role && (
                  <span className="truncate text-xs text-zinc-500">{lane.role}</span>
                )}
                {lane.overCapacity && (
                  <span
                    className="inline-flex items-center gap-1 text-xs font-medium text-amber-300"
                    title="Assigned more than capacity"
                  >
                    <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
                    Over capacity
                  </span>
                )}
              </div>

              {/* Counts */}
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-500">
                <span>
                  <span className="tabular-nums text-sky-300">{lane.counts.todo}</span>{' '}
                  To Do
                </span>
                <span>
                  <span className="tabular-nums text-accent-fg">
                    {lane.counts.inProgress}
                  </span>{' '}
                  In Progress
                </span>
                <span>
                  <span className="tabular-nums text-emerald-300">
                    {lane.counts.done}
                  </span>{' '}
                  Done
                </span>
                {lane.counts.blocked > 0 && (
                  <span className="text-red-300">
                    <span className="tabular-nums">{lane.counts.blocked}</span> blocked
                  </span>
                )}
                {lane.storyPoints > 0 && (
                  <span>
                    <span className="tabular-nums text-zinc-300">
                      {lane.storyPoints}
                    </span>{' '}
                    SP
                  </span>
                )}
              </div>
            </div>

            {/* Capacity (only meaningful for real people): occupied / capacity → leftover */}
            {!lane.isUnassigned && (
              <div className="w-56 shrink-0">
                <div className="flex items-center justify-between gap-2 text-xs">
                  <span className="text-zinc-500">
                    occupied{' '}
                    <span className="tabular-nums text-zinc-300">
                      {lane.assignedDays}d
                    </span>
                    <span className="text-zinc-600"> / {lane.capacityDays}d</span>
                  </span>
                  <span
                    className={cn(
                      'inline-flex items-center gap-0.5 font-medium tabular-nums',
                      lane.remainingDays < 0
                        ? 'text-red-300'
                        : lane.remainingDays === 0
                          ? 'text-amber-300'
                          : 'text-emerald-300',
                    )}
                    title="Leftover bandwidth (capacity − occupied)"
                  >
                    → leftover {lane.remainingDays}d
                  </span>
                </div>
                <div className="mt-1">
                  <CapacityBar
                    assigned={lane.assignedDays}
                    capacity={lane.capacityDays}
                    over={lane.overCapacity}
                  />
                </div>
              </div>
            )}
          </header>

          {/* Cards */}
          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {lane.tasks.map((t) => (
              <SprintBoardCard
                key={t.id}
                task={t}
                goalName={t.goalId ? goalNames.get(t.goalId) : undefined}
                subtaskProgress={subtaskProgress.get(t.id)}
                onOpen={onOpen}
                onSetStatus={onSetStatus}
                onToggleBlocked={onToggleBlocked}
                onRemoveFromSprint={onRemoveFromSprint}
                onToggleAdhoc={onToggleAdhoc}
                onAssignOwner={onAssignOwner}
                people={people}
                busy={busy}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

export default SprintBoardByPerson;
